import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Container, Row, Col, Form, Table, Button, Spinner, Alert } from 'react-bootstrap'
import moment from 'moment'
import withLoginRequired from '../../HOC/LoginModal'
import { getEventListDropDownV1, getEventParticipants } from '../../utils/config/API'

const getFieldValue = (item, keys, fallback = '') => {
  const matchingKey = keys.find(
    (key) => Object.prototype.hasOwnProperty.call(item, key) && item[key] !== null && item[key] !== undefined
  )
  return matchingKey ? item[matchingKey] : fallback
}

const buildCsvValue = (value) => {
  const normalized = value === null || value === undefined ? '' : `${value}`
  return `"${normalized.replace(/"/g, '""')}"`
}

const formatEventLabel = (eventItem) => {
  const name = eventItem.NAME || eventItem.name || eventItem.label || 'Unnamed Event'
  const dateTime = getFieldValue(eventItem, ['DATE_TIME', 'date_time', 'dateTime', 'EVENT_DATE_TIME', 'eventDateTime'])
  if (!dateTime) {
    return name
  }
  const parsed = moment(dateTime)
  if (!parsed.isValid()) {
    return name
  }
  return `${name} (${parsed.format('DD MMM YYYY, hh:mm A')})`
}

const EventParticipants = () => {
  const token = useMemo(() => localStorage.getItem('token'), [])

  const [events, setEvents] = useState([])
  const [selectedEventId, setSelectedEventId] = useState('')
  const [selectedEventName, setSelectedEventName] = useState('')
  const [participants, setParticipants] = useState([])
  const [loadingEvents, setLoadingEvents] = useState(false)
  const [loadingParticipants, setLoadingParticipants] = useState(false)
  const [eventError, setEventError] = useState('')
  const [participantError, setParticipantError] = useState('')

  const authConfig = useMemo(() => {
    return token
      ? {
          headers: {
            authorization: `Bearer ${token}`,
          },
        }
      : {}
  }, [token])

  useEffect(() => {
    const fetchEvents = async () => {
      setLoadingEvents(true)
      setEventError('')
      try {
        const response = await axios.get(getEventListDropDownV1, authConfig)
        const list = response?.data?.body || response?.data?.data || []
        setEvents(Array.isArray(list) ? list : [])
      } catch (error) {
        setEventError('Unable to load events. Please refresh the page or try again later.')
      } finally {
        setLoadingEvents(false)
      }
    }

    fetchEvents()
  }, [authConfig])

  const fetchParticipants = async (eventId) => {
    setLoadingParticipants(true)
    setParticipantError('')
    try {
      const response = await axios.get(`${getEventParticipants}${eventId}/participants`, authConfig)
      const list = response?.data?.body || response?.data?.data || []
      setParticipants(Array.isArray(list) ? list : [])
    } catch (error) {
      console.log('Error fetching participants', error)
      setParticipantError('Unable to load participants for the selected event.')
      setParticipants([])
    } finally {
      setLoadingParticipants(false)
    }
  }

  const handleEventChange = (event) => {
    const eventId = event.target.value
    setSelectedEventId(eventId)
    setParticipants([])
    setParticipantError('')

    if (!eventId) {
      setSelectedEventName('')
      return
    }

    const matchedEvent =
      events.find((item) => `${item.ID || item.id}` === eventId) ||
      events.find((item) => `${item.value}` === eventId)

    setSelectedEventName(matchedEvent?.NAME || matchedEvent?.name || matchedEvent?.label || '')

    fetchParticipants(eventId)
  }

  const downloadCsv = () => {
    if (!participants.length || loadingParticipants) {
      return
    }

    const csvFields = [
      { header: 'firstname', keys: ['first_name', 'firstname', 'FIRST_NAME', 'FIRSTNAME', 'firstName'] },
      { header: 'lastname', keys: ['last_name', 'lastname', 'LAST_NAME', 'LASTNAME', 'lastName'] },
      {
        header: 'phone',
        keys: [
          'phone',
          'PHONE',
          'mobile',
          'MOBILE',
          'contact_number',
          'CONTACT_NUMBER',
          'phone_number',
          'PHONE_NUMBER',
        ],
      },
      { header: 'email', keys: ['email', 'EMAIL', 'Email'] },
      { header: 'city', keys: ['city', 'CITY', 'City', 'user_city', 'USER_CITY'] },
      { header: 'grade', keys: ['grade', 'GRADE', 'Grade', 'user_grade', 'USER_GRADE'] },
      { header: 'curriculum', keys: ['curriculum', 'CURRICULUM', 'Curriculum', 'board', 'BOARD'] },
      { header: 'school', keys: ['school', 'SCHOOL', 'School', 'school_name', 'SCHOOL_NAME'] },
    ]

    const header = csvFields.map((field) => buildCsvValue(field.header))
    const rows = participants.map((participant) => {
      return csvFields
        .map((field) => {
          const value = getFieldValue(participant, field.keys, '-')
          return buildCsvValue(value)
        })
        .join(',')
    })

    const csvContent = [header.join(','), ...rows].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    const sanitizedName =
      selectedEventName || events.find((item) => `${item.ID || item.id}` === selectedEventId)?.NAME || 'event-participants'
    const fileName = `${sanitizedName.replace(/[^\w-]+/g, '_') || 'event-participants'}.csv`
    anchor.href = url
    anchor.setAttribute('download', fileName)
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(url)
  }

  const renderParticipants = () => {
    if (!selectedEventId) {
      return (
        <tr>
          <td colSpan={4} className="text-center">
            Select an event to see participants.
          </td>
        </tr>
      )
    }

    if (loadingParticipants) {
      return (
        <tr>
          <td colSpan={4} className="text-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading participants...
          </td>
        </tr>
      )
    }

    if (!participants.length) {
      return (
        <tr>
          <td colSpan={4} className="text-center">
            No participants found for this event.
          </td>
        </tr>
      )
    }

    return participants.map((participant, index) => {
      const firstName = getFieldValue(participant, ['first_name', 'firstname', 'FIRST_NAME', 'FIRSTNAME', 'firstName'])
      const lastName = getFieldValue(participant, ['last_name', 'lastname', 'LAST_NAME', 'LASTNAME', 'lastName'])
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || '-'
      const email = getFieldValue(participant, ['email', 'EMAIL', 'Email'], '-')
      const phone = getFieldValue(
        participant,
        ['phone', 'PHONE', 'mobile', 'MOBILE', 'contact_number', 'CONTACT_NUMBER'],
        '-'
      )

      return (
        <tr key={`${participant.id || participant.ID || index}`}>
          <td>{index + 1}</td>
          <td>{fullName}</td>
          <td>{email || '-'}</td>
          <td>{phone || '-'}</td>
        </tr>
      )
    })
  }

  return (
    <Container fluid className="w-100">
      <Row className="heading mb-3">
        <h3 className="title">Event Participants</h3>
      </Row>

      <Row className="mb-3">
        <Col xs={12}>
          <Form onSubmit={(e) => e.preventDefault()}>
            <Form.Label>Select Event</Form.Label>
            <Form.Select value={selectedEventId} onChange={handleEventChange} disabled={loadingEvents}>
              <option value="">{loadingEvents ? 'Loading events...' : 'Select an event'}</option>
              {events.map((eventItem) => (
                <option key={eventItem.ID || eventItem.id || eventItem.value} value={eventItem.ID || eventItem.id || eventItem.value}>
                  {formatEventLabel(eventItem)}
                </option>
              ))}
            </Form.Select>
          </Form>
        </Col>
      </Row>

      {eventError && (
        <Row className="mb-3">
          <Col md={6} lg={4}>
            <Alert variant="danger" className="mb-0">
              {eventError}
            </Alert>
          </Col>
        </Row>
      )}

      {participantError && (
        <Row className="mb-3">
          <Col md={6} lg={4}>
            <Alert variant="danger" className="mb-0">
              {participantError}
            </Alert>
          </Col>
        </Row>
      )}

      {selectedEventId && (
        <Row className="mb-3">
          <Col className="d-flex gap-2 flex-wrap align-items-center justify-content-between">
            <div>
              <strong>Participants:</strong> {participants.length}
            </div>
            <Button variant="primary" onClick={downloadCsv} disabled={!participants.length || loadingParticipants}>
              Download CSV
            </Button>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Table responsive className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
              </tr>
            </thead>
            <tbody>{renderParticipants()}</tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default withLoginRequired(EventParticipants)
